import React from 'react';
import { View, StyleSheet } from 'react-native';
import Svg, { Path, Rect, G, Circle } from 'react-native-svg';

export type PaymentMethodBrandId = string;

interface PaymentMethodBrandIconProps {
  methodId: PaymentMethodBrandId;
  /** Outer box size (SVG scales inside). Default 32. */
  size?: number;
  /** `symbol` = logo only (e.g. white Apple on dark buttons). Default `mark` = full tile for list rows. */
  display?: 'mark' | 'symbol';
}

/**
 * Wallet-style SVG marks for checkout lists (not official trademark lockups).
 * Uses widely recognized color patterns and simple geometry (Apple silhouette, Google colors, etc.).
 */
const PaymentMethodBrandIcon: React.FC<PaymentMethodBrandIconProps> = ({ methodId, size = 32, display = 'mark' }) => {
  const s = size;

  switch (methodId) {
    case 'apple_pay':
      if (display === 'symbol') {
        return (
          <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="Apple Pay">
            <Svg width={s} height={s} viewBox="0 0 24 24">
              <G transform="translate(12,12.2) scale(0.78) translate(-12,-12)">
                <Path
                  fill="#FFFFFF"
                  d="M16.5 10.2c-.02 1.98-1.08 3.48-2.65 3.48-1.25 0-1.76-.67-3.28-.67-1.5 0-1.98.67-3.23.67-1.58 0-2.72-1.65-2.72-3.8 0-2.45 1.35-4.35 3.35-4.35 1.05 0 1.83.45 2.45.45.6 0 1.73-.48 2.92-.48.47 0 2.16.04 3.26 1.58-2.6 1.43-2.18 4.2.9 5.12zm-4.1-7.7c.52-.62 1.35-1.05 2.15-1.05.1 1.05-.35 2.05-.95 2.78-.55.65-1.45 1.15-2.33 1.08-.12-1.02.4-2.05 1.13-2.81z"
                />
              </G>
            </Svg>
          </View>
        );
      }
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="Apple Pay">
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <Rect x="0.5" y="0.5" width="23" height="23" rx="5" fill="#000000" />
            <G transform="translate(12,12.2) scale(0.62) translate(-12,-12)">
              <Path
                fill="#FFFFFF"
                d="M16.5 10.2c-.02 1.98-1.08 3.48-2.65 3.48-1.25 0-1.76-.67-3.28-.67-1.5 0-1.98.67-3.23.67-1.58 0-2.72-1.65-2.72-3.8 0-2.45 1.35-4.35 3.35-4.35 1.05 0 1.83.45 2.45.45.6 0 1.73-.48 2.92-.48.47 0 2.16.04 3.26 1.58-2.6 1.43-2.18 4.2.9 5.12zm-4.1-7.7c.52-.62 1.35-1.05 2.15-1.05.1 1.05-.35 2.05-.95 2.78-.55.65-1.45 1.15-2.33 1.08-.12-1.02.4-2.05 1.13-2.81z"
              />
            </G>
          </Svg>
        </View>
      );

    case 'google_pay':
      if (display === 'symbol') {
        return (
          <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="Google Pay">
            <Svg width={s} height={s} viewBox="0 0 24 24">
              <Rect x="4.2" y="5.5" width="2.9" height="13" rx="0.55" fill="#4285F4" />
              <Rect x="8" y="5.5" width="2.9" height="13" rx="0.55" fill="#EA4335" />
              <Rect x="11.8" y="5.5" width="2.9" height="13" rx="0.55" fill="#FBBC04" />
              <Rect x="15.6" y="5.5" width="2.9" height="13" rx="0.55" fill="#34A853" />
            </Svg>
          </View>
        );
      }
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="Google Pay">
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <Rect x="0.5" y="0.5" width="23" height="23" rx="5" fill="#FFFFFF" stroke="#E8EAED" strokeWidth="1" />
            <Rect x="4.2" y="6" width="2.9" height="12" rx="0.55" fill="#4285F4" />
            <Rect x="8" y="6" width="2.9" height="12" rx="0.55" fill="#EA4335" />
            <Rect x="11.8" y="6" width="2.9" height="12" rx="0.55" fill="#FBBC04" />
            <Rect x="15.6" y="6" width="2.9" height="12" rx="0.55" fill="#34A853" />
          </Svg>
        </View>
      );

    case 'venmo':
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="Venmo">
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <Rect width="24" height="24" rx="5" fill="#3396EC" />
            <Path
              fill="#FFFFFF"
              d="M17.2 6.2H14.1L10.5 16.5 8.4 6.2H5.8l3.4 11.6h3.6l3.4-11.6z"
            />
          </Svg>
        </View>
      );

    case 'paypal':
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="PayPal">
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <Rect width="24" height="24" rx="5" fill="#003087" />
            <Path fill="#009CDE" d="M8.2 7.2h4.8c1.35 0 2.35.85 2.35 2 0 1.45-1.05 2.35-2.75 2.35H9.6L9 17.2H6.4L8.2 7.2z" />
            <Path fill="#001C64" d="M11.4 9.8h2c.55 0 .9-.3.9-.75 0-.5-.4-.85-1.1-.85h-1.4l-.4 1.6z" />
          </Svg>
        </View>
      );

    case 'cash_app':
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="Cash App">
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <Rect width="24" height="24" rx="5" fill="#00D632" />
            <Path
              fill="#FFFFFF"
              d="M12 6.5c-2.2 0-4 1.4-4 3.1 0 .95.55 1.85 1.45 2.35v1.25c0 .45.35.8.8.8h.25v-1.05c.35.08.7.12 1.1.12 2.2 0 4-1.4 4-3.1S14.2 6.5 12 6.5zm0 4.9c-1.2 0-2.15-.6-2.15-1.35S10.8 9.7 12 9.7s2.15.6 2.15 1.35S13.2 11.4 12 11.4z"
            />
            <Path fill="#FFFFFF" d="M9.5 16.2h5v1.8h-5v-1.8z" />
          </Svg>
        </View>
      );

    case 'zelle':
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="Zelle">
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <Rect width="24" height="24" rx="5" fill="#6D1ED4" />
            <Path
              fill="#FFFFFF"
              d="M6.8 7.2h3.2l3.8 5.4 3.8-5.4h3.2L14.3 12.8 17.2 18h-3.1l-2.1-3.4-2.1 3.4H6.8l3-5.2-3-5.6z"
            />
          </Svg>
        </View>
      );

    case 'pay_later':
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="Confirm booking">
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <Rect x="1" y="1" width="22" height="22" rx="5" fill="#F2F2F7" stroke="#C7C7CC" strokeWidth="1" />
            <Circle cx="12" cy="12" r="6.5" stroke="#007AFF" strokeWidth="1.8" fill="none" />
            <Path stroke="#007AFF" strokeWidth="1.8" strokeLinecap="round" d="M12 8.2V12l2.8 1.6" />
          </Svg>
        </View>
      );

    case 'qr_code':
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="QR code">
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <Rect x="0.5" y="0.5" width="23" height="23" rx="5" fill="#FFFFFF" stroke="#1C1C1E" strokeWidth="1" />
            <Rect x="4" y="4" width="5" height="5" fill="#1C1C1E" />
            <Rect x="15" y="4" width="5" height="5" fill="#1C1C1E" />
            <Rect x="4" y="15" width="5" height="5" fill="#1C1C1E" />
            <Rect x="11" y="11" width="3" height="3" fill="#1C1C1E" />
            <Rect x="15" y="11" width="3" height="3" fill="#1C1C1E" />
            <Rect x="11" y="15" width="3" height="3" fill="#1C1C1E" />
            <Rect x="15" y="15" width="5" height="5" fill="#1C1C1E" />
          </Svg>
        </View>
      );

    case 'sms_request':
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="SMS">
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <Rect width="24" height="24" rx="5" fill="#34C759" />
            <Path
              fill="#FFFFFF"
              d="M6 8.5h12v6H9.5L6 18v-9.5zm2 2v2h8v-2H8zm0 3v1.5h5V13.5H8z"
            />
          </Svg>
        </View>
      );

    case 'app_chat':
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="Chat">
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <Rect width="24" height="24" rx="5" fill="#007AFF" />
            <Path
              fill="#FFFFFF"
              d="M7 8.5h10c.55 0 1 .45 1 1v3.5c0 .55-.45 1-1 1h-3.2L9 17.2V14H7c-.55 0-1-.45-1-1V9.5c0-.55.45-1 1-1z"
            />
          </Svg>
        </View>
      );

    case 'cash':
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="Cash">
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <Rect width="24" height="24" rx="5" fill="#1a7f3c" />
            <Rect x="4.5" y="6.5" width="15" height="11" rx="1.5" fill="#fef9c3" />
            <Path
              stroke="#166534"
              strokeWidth="1.4"
              strokeLinecap="round"
              d="M12 8.2v7.6M9.2 10.8c.6-1.2 5.6-1.2 6.2 0M9.2 13.2c.6 1.2 5.6 1.2 6.2 0"
            />
          </Svg>
        </View>
      );

    case 'credit_card':
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="Card">
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <Rect x="1" y="4" width="22" height="16" rx="2.5" fill="#1e293b" />
            <Rect x="3" y="7.5" width="5" height="3.5" rx="0.6" fill="#E2BF7A" />
            <Rect x="3" y="13" width="14" height="1.8" rx="0.4" fill="rgba(255,255,255,0.25)" />
            <Rect x="3" y="15.5" width="10" height="1.8" rx="0.4" fill="rgba(255,255,255,0.15)" />
          </Svg>
        </View>
      );

    default:
      return (
        <View style={[styles.box, { width: s, height: s }]} accessibilityLabel="Payment">
          <Svg width={s} height={s} viewBox="0 0 24 24">
            <Rect x="1" y="4" width="22" height="16" rx="2.5" fill="#334155" />
            <Rect x="3" y="7.5" width="5" height="3.5" rx="0.6" fill="#cbd5e1" />
            <Rect x="3" y="13" width="14" height="1.8" rx="0.4" fill="rgba(255,255,255,0.2)" />
          </Svg>
        </View>
      );
  }
};

const styles = StyleSheet.create({
  box: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default PaymentMethodBrandIcon;
